// SPDX-License-Identifier: AGPL-3.0-or-later
import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {makeAutoObservable, runInAction} from 'mobx';

interface GeoIPData {
	countryCode: string | null;
	regionCode: string | null;
	latitude: string | null;
	longitude: string | null;
	ageRestrictedGeos: Array<{
		countryCode: string;
		regionCode: string | null;
	}>;
	ageBlockedGeos: Array<{
		countryCode: string;
		regionCode: string | null;
	}>;
}
interface ConnectionGeoCoordinates {
	latitude: string | null;
	longitude: string | null;
}
class GeoIP {
	countryCode: string | null = null;
	regionCode: string | null = null;
	latitude: string | null = null;
	longitude: string | null = null;
	ageRestrictedGeos: Array<{
		countryCode: string;
		regionCode: string | null;
	}> = [];
	ageBlockedGeos: Array<{
		countryCode: string;
		regionCode: string | null;
	}> = [];
	loaded = false;
	error: string | null = null;
	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}
	async fetchGeoData(): Promise<void> {
		try {
			const endpoint = RuntimeConfig.apiEndpoint.replace(/\/+$/, '');
			if (!endpoint) {
				throw new Error('API endpoint is not configured yet');
			}
			const response = await fetch(`${endpoint}/ip`, {signal: AbortSignal.timeout(5000)});
			if (!response.ok) {
				throw new Error(`Failed to fetch geo data: ${response.statusText}`);
			}
			const data: GeoIPData = await response.json();
			runInAction(() => {
				this.countryCode = data.countryCode;
				this.regionCode = data.regionCode;
				this.latitude = data.latitude;
				this.longitude = data.longitude;
				this.ageRestrictedGeos = data.ageRestrictedGeos;
				this.ageBlockedGeos = data.ageBlockedGeos;
				this.loaded = true;
				this.error = null;
			});
		} catch (error) {
			runInAction(() => {
				this.countryCode = null;
				this.regionCode = null;
				this.latitude = null;
				this.longitude = null;
				this.ageRestrictedGeos = [];
				this.ageBlockedGeos = [];
				this.loaded = true;
				this.error = error instanceof Error ? error.message : 'Unknown error';
			});
			throw error;
		}
	}
	applyConnectionFallbackCoordinates(data: ConnectionGeoCoordinates): void {
		if (data.latitude === null || data.longitude === null) {
			return;
		}
		if (this.latitude !== null && this.longitude !== null) {
			return;
		}
		runInAction(() => {
			if (this.latitude === null) {
				this.latitude = data.latitude;
			}
			if (this.longitude === null) {
				this.longitude = data.longitude;
			}
			this.loaded = true;
		});
	}
	isBlocked(): boolean {
		if (!this.countryCode) return false;
		return this.ageBlockedGeos.some((geo) => {
			if (geo.countryCode !== this.countryCode) return false;
			if (geo.regionCode === null) return true;
			return geo.regionCode === this.regionCode;
		});
	}
}
export default new GeoIP();
